import React, { useMemo } from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type {
  GenericPickerConfig,
  OptionSource,
} from '@ns/plugin-picker-toolbox-contracts';

import { ChoiceField } from '../choice-field';

/**
 * The uiOptions a template author sets on a picker field via `ui:options`. Only
 * `dependsOn` is generic here (the cascade source refs); the adapter-specific
 * `source` config is supplied by the wrapper that binds a concrete OptionSource.
 */
export type ChoiceFieldUiOptions = {
  /** The field keys this picker depends on, in cascade order. Empty = root. */
  dependsOn?: readonly string[];
};

/**
 * A React hook that yields the source for one field instance. It is called in
 * the bound Field render so wrappers can resolve their ApiRefs without global
 * mutable state.
 */
export type UseChoiceSource<TItem, TValue> = () => OptionSource<TItem, TValue>;

const sourceLifecycleKeys = new WeakMap<object, number>();
let nextSourceLifecycleKey = 0;

/**
 * Gives each source object a stable, garbage-collectable lifecycle identity.
 * A WeakMap avoids retaining adapters after their fields unmount. A fresh
 * source on every render still produces a finite render because this mapping
 * does not update React state.
 */
function lifecycleKeyForSource(source: object): number {
  const existing = sourceLifecycleKeys.get(source);
  if (existing !== undefined) return existing;

  nextSourceLifecycleKey += 1;
  sourceLifecycleKeys.set(source, nextSourceLifecycleKey);
  return nextSourceLifecycleKey;
}

/**
 * Resolves the set of valid form-field keys, the owner of ref validation.
 * Neither available source knows which fields exist on its own,
 * as measured in a running scaffolder:
 *
 * - `formContext.formData` spans every step (the Stepper injects `stepsState`)
 *   but holds only the fields the user has TOUCHED. An untouched sibling is
 *   missing from it.
 * - `registry.rootSchema.properties` lists every field of the CURRENT step,
 *   touched or not, but knows nothing of the other steps.
 *
 * Either alone reports a false "unknown field": formData for an untouched
 * sibling, rootSchema for a cross-step ref. Their union covers both, and a key
 * in neither is still an error, so a genuine typo stays visible to the template
 * author.
 */
export function validKeysFrom(sources: {
  formData?: Record<string, unknown>;
  schemaProperties?: Record<string, unknown>;
}): { formData: Record<string, unknown>; validKeys: ReadonlySet<string> } {
  const formData = sources.formData ?? {};
  return {
    formData,
    validKeys: new Set([
      ...Object.keys(formData),
      ...Object.keys(sources.schemaProperties ?? {}),
    ]),
  };
}

/**
 * The batteries-included Field component: adapts the scaffolder
 * `FieldExtensionComponentProps` to the generic `ChoiceField`, binding a source
 * from a hook. It derives the valid-keys set and cross-step form data from
 * `formContext.formData` (the ref-validation owner), maps the RJSF `onChange`,
 * and passes the current field value.
 *
 * Datasource-agnostic: the same component drives any OptionSource (local mock or
 * remote transport). It is a thin orchestration adapter over ChoiceField (the
 * primitive), matching the progressive-disclosure layering (Fields on top of
 * Primitives).
 */
export function ChoiceFormField<TItem, TValue, TConfig>(
  useSource: UseChoiceSource<TItem, TValue>,
  baseConfig: TConfig,
): (
  props: FieldExtensionComponentProps<TValue, ChoiceFieldUiOptions>,
) => React.ReactElement {
  return function BoundChoiceFormField(
    props: FieldExtensionComponentProps<TValue, ChoiceFieldUiOptions>,
  ): React.ReactElement {
    const { formContext, formData, onChange, required, schema, uiSchema } =
      props;
    const source = useSource();
    const dependsOn = uiSchema['ui:options']?.dependsOn;
    const sourceLifecycleKey = lifecycleKeyForSource(source);

    // The step's own schema: every field of THIS step, touched or not. The RJSF
    // types declare `registry.rootSchema` as always present, but a host that
    // renders the field directly (a test, a preview) can leave it out, so the
    // read is defensive despite what the types say.
    const schemaProperties = (
      props.registry as {
        rootSchema?: { properties?: Record<string, unknown> };
      }
    ).rootSchema?.properties;
    const { formData: crossStepFormData, validKeys } = useMemo(
      () =>
        validKeysFrom({
          formData: formContext?.formData,
          schemaProperties,
        }),
      [formContext, schemaProperties],
    );

    const config: GenericPickerConfig<TConfig> = useMemo(
      () => ({ dependsOn, source: baseConfig }),
      [dependsOn],
    );

    return (
      <ChoiceField
        key={sourceLifecycleKey}
        config={config}
        formData={crossStepFormData}
        label={schema.title ?? props.name}
        onChange={onChange}
        required={required}
        source={source}
        validKeys={validKeys}
        value={formData}
      />
    );
  };
}
